import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Site, UrlEntry, CheckRecord } from '@shared/types';
import {
  listSites,
  createSite,
  updateSite,
  deleteSite,
  listUrls,
  createUrl,
  updateUrl,
  deleteUrl,
  listUrlChecks,
} from '../api';
import { scoreColor, formatDate } from '../scoreUtils';

interface UrlWithHistory extends UrlEntry {
  latest?: CheckRecord;
  prev?: CheckRecord;
}

export default function SitesPage() {
  const [sites, setSites] = useState<Site[]>([]);
  const [urlsBySite, setUrlsBySite] = useState<Record<string, UrlWithHistory[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await listSites();
      setSites(s);
      const entries = await Promise.all(
        s.map(async (site) => {
          const urls = await listUrls(site.id);
          const withHist = await Promise.all(
            urls.map(async (u) => {
              let history: CheckRecord[] = [];
              try {
                history = await listUrlChecks(u.id);
              } catch {
                history = [];
              }
              const done = history
                .filter((h) => h.status === 'done' && h.totalScore != null)
                .sort((a, b) => (a.runAt < b.runAt ? 1 : -1));
              return { ...u, latest: done[0], prev: done[1] } as UrlWithHistory;
            }),
          );
          return [site.id, withHist] as const;
        }),
      );
      setUrlsBySite(Object.fromEntries(entries));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <div className="loading">読み込み中…</div>;

  return (
    <div className="page">
      <div className="page-head">
        <h1>クライアント一覧</h1>
        <Link to="/admin/check" className="btn btn--primary">
          + 単発チェック
        </Link>
      </div>

      {error && <div className="alert alert--error">{error}</div>}

      <AddSiteForm onAdded={load} />

      {sites.length === 0 ? (
        <p className="muted">まだクライアントが登録されていません。上のフォームから追加してください。</p>
      ) : (
        sites.map((site) => (
          <SiteCard
            key={site.id}
            site={site}
            urls={urlsBySite[site.id] ?? []}
            onChanged={load}
          />
        ))
      )}
    </div>
  );
}

function AddSiteForm({ onAdded }: { onAdded: () => void }) {
  const [name, setName] = useState('');
  const [clientName, setClientName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await createSite({
        name: name.trim(),
        clientName: clientName.trim() || null,
      });
      setName('');
      setClientName('');
      onAdded();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card add-form" onSubmit={submit}>
      <h3>クライアントを追加</h3>
      <div className="add-form__row">
        <input
          placeholder="管理名（例: コーポレートサイト）"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          placeholder="クライアント名（任意）"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
        />
        <button className="btn btn--primary" disabled={busy || !name.trim()}>
          追加
        </button>
      </div>
      {err && <div className="alert alert--error">{err}</div>}
    </form>
  );
}

function SiteCard({
  site,
  urls,
  onChanged,
}: {
  site: Site;
  urls: UrlWithHistory[];
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(site.name);
  const [clientName, setClientName] = useState(site.clientName ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function saveEdit() {
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await updateSite(site.id, {
        name: name.trim(),
        clientName: clientName.trim() || null,
      });
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    const urlCount = urls.length;
    const confirmMsg =
      urlCount > 0
        ? `「${site.name}」を削除します。登録済みのURL(${urlCount}件)とすべてのチェック履歴も削除されます。よろしいですか？`
        : `「${site.name}」を削除します。よろしいですか？`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteSite(site.id);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="card site-card">
      <div className="site-card__head">
        {editing ? (
          <div className="site-card__edit-form">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="管理名"
            />
            <input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="クライアント名（任意）"
            />
            <button className="btn btn--sm btn--primary" disabled={busy || !name.trim()} onClick={saveEdit}>
              保存
            </button>
            <button
              className="btn btn--sm btn--ghost"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setName(site.name);
                setClientName(site.clientName ?? '');
                setErr(null);
              }}
            >
              キャンセル
            </button>
          </div>
        ) : (
          <>
            <div>
              <h2>{site.name}</h2>
              {site.clientName && (
                <span className="site-card__client">{site.clientName}</span>
              )}
            </div>
            <div className="site-card__head-right">
              <span className="muted small">登録: {formatDate(site.createdAt)}</span>
              <button className="btn btn--sm btn--ghost" onClick={() => setEditing(true)}>
                編集
              </button>
              <button
                className="btn btn--sm btn--danger"
                disabled={busy}
                onClick={handleDelete}
              >
                削除
              </button>
            </div>
          </>
        )}
      </div>

      {err && <div className="alert alert--error">{err}</div>}

      {urls.length === 0 ? (
        <p className="muted small">URL未登録</p>
      ) : (
        <table className="url-table">
          <thead>
            <tr>
              <th>URL / ラベル</th>
              <th>最新スコア</th>
              <th>前回比</th>
              <th>最終チェック</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {urls.map((u) => (
              <UrlRow key={u.id} url={u} onChanged={onChanged} navigate={navigate} />
            ))}
          </tbody>
        </table>
      )}

      <AddUrlForm siteId={site.id} onAdded={onChanged} />
    </div>
  );
}

function UrlRow({
  url: u,
  onChanged,
  navigate,
}: {
  url: UrlWithHistory;
  onChanged: () => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [editing, setEditing] = useState(false);
  const [urlValue, setUrlValue] = useState(u.url);
  const [label, setLabel] = useState(u.label ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const score = u.latest?.totalScore ?? null;
  const prev = u.prev?.totalScore ?? null;
  const diff = score != null && prev != null ? score - prev : null;

  async function saveEdit() {
    if (!urlValue.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await updateUrl(u.id, { url: urlValue.trim(), label: label.trim() || null });
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`このURLとチェック履歴をすべて削除します。よろしいですか？\n${u.url}`)) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteUrl(u.id);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <tr>
        <td colSpan={5}>
          <div className="url-edit-form">
            <input
              value={urlValue}
              onChange={(e) => setUrlValue(e.target.value)}
              placeholder="https://example.com/page"
            />
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="ラベル（任意）"
            />
            <button className="btn btn--sm btn--primary" disabled={busy || !urlValue.trim()} onClick={saveEdit}>
              保存
            </button>
            <button
              className="btn btn--sm btn--ghost"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setUrlValue(u.url);
                setLabel(u.label ?? '');
                setErr(null);
              }}
            >
              キャンセル
            </button>
          </div>
          {err && <div className="alert alert--error">{err}</div>}
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <div className="url-cell">
          <span className="url-cell__url">{u.url}</span>
          {u.label && <span className="url-cell__label">{u.label}</span>}
        </div>
      </td>
      <td>
        {score != null ? (
          <span className="score-pill" style={{ backgroundColor: scoreColor(score) }}>
            {score}
          </span>
        ) : (
          <span className="muted small">未実施</span>
        )}
      </td>
      <td>
        {diff != null ? (
          <span className={diff > 0 ? 'diff diff--up' : diff < 0 ? 'diff diff--down' : 'diff'}>
            {diff > 0 ? '▲' : diff < 0 ? '▼' : '±'}
            {Math.abs(diff)}
          </span>
        ) : (
          <span className="muted small">—</span>
        )}
      </td>
      <td className="muted small">{u.latest ? formatDate(u.latest.runAt) : '—'}</td>
      <td className="url-table__actions">
        <button
          className="btn btn--sm"
          onClick={() =>
            navigate(`/admin/check?url=${encodeURIComponent(u.url)}&urlId=${u.id}`)
          }
        >
          チェック
        </button>
        <Link className="btn btn--sm btn--ghost" to={`/admin/urls/${u.id}/history`}>
          推移
        </Link>
        <button className="btn btn--sm btn--ghost" onClick={() => setEditing(true)}>
          編集
        </button>
        <button className="btn btn--sm btn--danger" disabled={busy} onClick={handleDelete}>
          削除
        </button>
      </td>
    </tr>
  );
}

function AddUrlForm({ siteId, onAdded }: { siteId: string; onAdded: () => void }) {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await createUrl({
        siteId,
        url: url.trim(),
        label: label.trim() || null,
      });
      setUrl('');
      setLabel('');
      onAdded();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="add-url-form" onSubmit={submit}>
      <input
        placeholder="https://example.com/page"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
      />
      <input
        placeholder="ラベル（任意）"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
      />
      <button className="btn btn--sm btn--primary" disabled={busy || !url.trim()}>
        URL追加
      </button>
      {err && <div className="alert alert--error">{err}</div>}
    </form>
  );
}
